/* eslint-disable react/no-multi-comp */
import { ReactNode, MouseEvent, useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';

import { Box } from '../Box';
import { Typography } from '../Typography';

import { LinkSize, LinkColor, LinkStroke, IconAlign } from './Link.types';
import { useStyles } from './Link.styles';

/**
 * Базовые свойства компонента {@link Link}
 */
export interface LinkBaseProps {
  /**
   * Размер ссылки.
   *
   * @defaultValue `'medium'`
   */
  size?: keyof typeof LinkSize;

  /**
   * Цветовая вариация ссылки.
   *
   * @defaultValue `'primary'`
   */
  variant?: keyof typeof LinkColor;

  /**
   * Определяет, должна ли ссылка иметь подчёркивание.
   *
   * @defaultValue `true`
   */
  stroke?: LinkStroke;

  /**
   * Если true, ссылка будет отключена и некликабельна.
   *
   * @defaultValue `false`
   */
  disabled?: boolean;

  /**
   * Содержимое ссылки.
   */
  children?: ReactNode | ReactNode[];

  /**
   * Иконка, отображаемая перед текстом ссылки.
   */
  startIcon?: ReactNode;

  /**
   * Иконка, отображаемая после текста ссылки.
   */
  endIcon?: ReactNode;

  /**
   * Заголовок страницы для навигации.
   */
  toPageTitle?: string;

  /**
   * Пользовательское действие при клике.
   */
  onCustomAction?: () => void;

  /**
   * Выравнивание иконки относительно текста.
   */
  iconAlign?: IconAlign;

  /**
   * Если true, ссылка отображается как кнопка "назад".
   */
  backLink?: boolean;

  /**
   * CSS свойство display.
   */
  display?: string;

  /**
   * Идентификатор для тестирования.
   */
  testId?: string;
}

type RouterLinkProps = LinkBaseProps & {
  /**
   * Путь для ссылки при использовании react-router.
   */
  to: string;
};

type HrefLinkProps = LinkBaseProps & {
  /**
   * URL для ссылки при использовании обычного якорного тега.
   */
  href: string;

  /**
   * Атрибут target для якорного тега.
   */
  target?: string;
};

type ButtonLinkProps = LinkBaseProps & {
  /**
   * Обработчик клика для ссылки при рендеринге её как кнопки.
   */
  onClick: (event: MouseEvent<HTMLElement>) => void;

  /**
   * Дополнительные классы для ссылки при рендеринге её как кнопки.
   */
  className?: string;
};

export type LinkProps = RouterLinkProps | HrefLinkProps | ButtonLinkProps;

const isRouterProps = (props: LinkProps): props is RouterLinkProps =>
  Object.prototype.hasOwnProperty.call(props, 'to');
const isHrefProps = (props: LinkProps): props is HrefLinkProps =>
  Object.prototype.hasOwnProperty.call(props, 'href');
const isButtonProps = (props: LinkProps): props is ButtonLinkProps =>
  Object.prototype.hasOwnProperty.call(props, 'onClick');

const typographyMapping: Record<
  'large' | 'medium' | 'small' | 'extraSmall',
  'subtitle1' | 'subtitle2' | 'body3' | 'caption'
> = {
  large: 'subtitle1',
  medium: 'subtitle2',
  small: 'body3',
  extraSmall: 'caption',
};

/**
 * Динамическая загрузка компонента ссылки при использовании с react-router
 */
const loadReactRouterLink = () =>
  import('react-router').then(({ Link: ReactRouterLink }) => ReactRouterLink);

type RouterLinkComponent = React.FC<
  RouterLinkProps & React.HTMLAttributes<HTMLElement>
>;

/**
 * Компонент RouterLink для реализации по умолчанию
 */

const DefaultRouterLink: RouterLinkComponent = ({ to, children, ...props }) => (
  <a href={to} {...props}>
    {children}
  </a>
);

let CacheRouterLink: RouterLinkComponent | null = null;

/**
 * Вериатильный компонент Link, который может рендериться как ссылка роутера, обычный якорный тег с href или как ссылка в виде кнопки.
 * Возможно использование с react-router `[props.to]`. Для работы установите: `react-router`.
 *
 * @public
 * @param size - Размер ссылки.
 * @param variant - Цветовая вариация ссылки.
 * @param stroke - Определяет, должна ли ссылка иметь подчёркивание.
 * @param disabled - Если true, ссылка будет отключена и некликабельна.
 * @param children - Содержимое ссылки.
 * @param startIcon - Иконка, отображаемая перед текстом ссылки.
 * @param endIcon - Иконка, отображаемая после текста ссылки.
 * @param toPageTitle - Заголовок страницы для навигации.
 * @param onCustomAction - Пользовательское действие при клике.
 * @param iconAlign - Выравнивание иконки относительно текста.
 * @param backLink - Если true, ссылка отображается как кнопка "назад".
 * @param display - CSS свойство display.
 * @param testId - Необязательный идентификатор для автоматизированного тестирования.
 * @param props - Остальные свойства в зависимости от типа ссылки (RouterLinkProps, HrefLinkProps, ButtonLinkProps).
 * @returns  React-компонент, рендерящий ссылку на основе предоставленных пропсов.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link LinkProps}
 */
export const Link = (props: LinkProps) => {
  const {
    size = 'medium',
    variant = 'primary',
    disabled = false,
    stroke = true,
    startIcon,
    endIcon,
    children,
    onCustomAction,
    iconAlign,
    backLink,
    display,
    testId,
  } = props;

  const [RouterLink, setRouterLink] = useState<RouterLinkComponent>(
    () => CacheRouterLink ?? DefaultRouterLink,
  );

  useEffect(() => {
    if (CacheRouterLink) return;

    let mounted = true;
    loadReactRouterLink()
      .then((ReactRouterLink) => {
        if (!mounted) return;

        CacheRouterLink = ReactRouterLink as RouterLinkComponent;
        if (CacheRouterLink !== null) {
          setRouterLink(() => CacheRouterLink as RouterLinkComponent);
        }
      })
      .catch(() => {
        CacheRouterLink = DefaultRouterLink;
      });

    return () => {
      mounted = false;
    };
  }, []);

  const { classes } = useStyles({
    size: LinkSize[size],
    color: LinkColor[variant],
    stroke: variant === 'simple' ? false : stroke,
    disabled,
    iconAlign,
    backLink,
    display,
  });

  const handleAction = useCallback(
    (e?: MouseEvent) => {
      if (disabled) {
        e?.preventDefault();
        return;
      }
      onCustomAction?.();
    },
    [onCustomAction, disabled],
  );

  const buttonOnClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      if (isButtonProps(props)) {
        props.onClick(e);
        handleAction();
      }
    },
    [props, handleAction, disabled],
  );

  const content = (
    <Box
      {...(variant === 'simple'
        ? { display: 'inline' }
        : { display: 'flex', alignItems: 'center' })}
    >
      {startIcon && (
        <Box
          className={classNames(classes.icon, !!children && classes.startIcon)}
        >
          {startIcon}
        </Box>
      )}
      {children && (
        <Typography
          color="inherit"
          component="span"
          variant={typographyMapping[size]}
          data-testid="link-typography"
        >
          {children}
        </Typography>
      )}
      {endIcon && (
        <Box
          className={classNames(classes.icon, !!children && classes.endIcon)}
        >
          {endIcon}
        </Box>
      )}
    </Box>
  );

  const styles = classNames(
    classes.link,
    isButtonProps(props) && props.className,
    disabled && classes.disabledLink,
    // isButtonProps(props) && classes.linkLikeButton,
  );

  if (isRouterProps(props)) {
    return (
      <RouterLink
        onClick={handleAction}
        to={props.to}
        className={styles}
        data-testid={testId || 'router-link'}
      >
        {content}
      </RouterLink>
    );
  }

  if (isHrefProps(props)) {
    return (
      <a
        href={props.href}
        target={props.target}
        className={styles}
        data-testid={testId || 'link-a'}
      >
        {content}
      </a>
    );
  }

  return (
    <a
      href="#"
      className={styles}
      onClick={buttonOnClick}
      data-testid={testId || 'link-btn'}
    >
      {content}
    </a>
  );
};
